/**
 * Macro #21 §5 — Purchase selection occupancy guard (fail-fast UX).
 *
 * A PURE, presentation-only guard that answers ONE question from data
 * ALREADY on the page: does the drawn rectangle overlap a canonical
 * (indexer-sourced) parcel that already exists on-chain?
 *
 * FROZEN LAW (ratified by Claudio, Opzione A):
 *   - Positive evidence of occupancy in the canonical overlay → block the
 *     purchase immediately (fail-fast), BEFORE reaching the wallet.
 *   - This holds for land owned by ANOTHER wallet AND for land already
 *     owned by the buyer itself (already-minted land = not purchasable
 *     again).
 *   - ABSENCE of overlap in the overlay is NOT a proof of availability:
 *     the indexer may lag a few blocks. Therefore quote + preflight remain
 *     mandatory, and the contract/chain (ErrOverlap) stays the final
 *     authority. This guard NEVER grants availability.
 *
 * This module owns NO state, performs NO fetch, and is NOT a second
 * authority — it merely reads the parcels the viewer already loaded.
 */
import type { GridSelection } from "./grid-selection";

/** Minimal canonical parcel footprint consumed by the guard. */
export interface OccupancyRect {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Owner wallet (lowercase or not) — used only for messaging. */
  owner?: string | null;
}

/** Half-open integer rectangle overlap test (cells [x, x+w) × [y, y+h)). */
export function rectsOverlap(a: OccupancyRect, b: GridSelection): boolean {
  return (
    a.x < b.x + b.w &&
    b.x < a.x + a.w &&
    a.y < b.y + b.h &&
    b.y < a.y + a.h
  );
}

/** Canonical parcels the selection overlaps (positive occupancy evidence). */
export function findOccupancyOverlaps(
  selection: GridSelection | null,
  parcels: readonly OccupancyRect[],
): OccupancyRect[] {
  if (selection == null) return [];
  if (selection.w < 1 || selection.h < 1) return [];
  return parcels.filter((p) => p.w > 0 && p.h > 0 && rectsOverlap(p, selection));
}

export interface OccupancyGuard {
  /** Fail-fast: the selection has positive canonical occupancy evidence. */
  blocked: boolean;
  /** How many canonical parcels the selection overlaps. */
  overlapCount: number;
  /** Overlaps land owned by the current buyer. */
  ownedBySelf: boolean;
  /** Overlaps land owned by a different wallet. */
  ownedByOther: boolean;
}

function _norm(a: string | null | undefined): string {
  return typeof a === "string" ? a.trim().toLowerCase() : "";
}

/**
 * Derive the fail-fast occupancy guard for a candidate selection.
 * `buyer` (optional) only differentiates the self/other messaging; the
 * BLOCK decision is identical either way (both are non-purchasable).
 */
export function deriveOccupancyGuard(
  selection: GridSelection | null,
  parcels: readonly OccupancyRect[],
  buyer?: string | null,
): OccupancyGuard {
  const overlaps = findOccupancyOverlaps(selection, parcels);
  const b = _norm(buyer);
  let ownedBySelf = false;
  let ownedByOther = false;
  for (const p of overlaps) {
    if (b && _norm(p.owner) === b) ownedBySelf = true;
    else ownedByOther = true;
  }
  return {
    blocked: overlaps.length > 0,
    overlapCount: overlaps.length,
    ownedBySelf,
    ownedByOther,
  };
}
